import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  LinearProgress,
  Alert,
  CssBaseline,
  MenuItem,
  Select,
} from "@mui/material";
import axios from "axios";
import { countries } from "countries-list";
import CreditsBanner from "../Components/CreditsNudge";
import {
  ContentBox,
  StyledButton,
  TitleText,
  SubtitleText,
  PageContainer,
} from "./Default.js";
import { Link } from "react-router-dom";
import YouTubeSearchResultsExportContent from "../Contents/YtSearchResultsExport.js";
import { message } from "../Constants/message.js";
import CommentSection from "../Components/CommentSection.js";

function YouTubeSearchResultsExport({ user, login, isLoggedIn }) {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [countryCode, setCountryCode] = useState("US");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [downloadUrl, setDownloadUrl] = useState("");
  const [credits, setCredits] = useState({
    remaining: 0,
    availed: 0,
    total: 0,
    loading: true,
  });

  useEffect(() => {
    const primaryInputText = localStorage.getItem("primaryInputText");
    const primaryDropDown = localStorage.getItem("primary-drop-down");
    if (primaryInputText) {
      setSearchKeyword(primaryInputText);
      localStorage.removeItem("primaryInputText");
    }
    if (primaryDropDown) {
      setCountryCode(primaryDropDown);
      localStorage.removeItem("primary-drop-down");
    }
  }, []);

  const countryOptions = Object.entries(countries).map(([code, { name }]) => ({
    code,
    name,
  }));

  const handleExport = async () => {
    setSuccess("");
    setError("");
    if (!isLoggedIn) {
      setError(message.loginPrompt);
      setTimeout(() => {
        login();
      }, 1500);
      return;
    }
    if (!searchKeyword.trim()) {
      setError("Please enter a search keyword.");
      return;
    }

    if (credits.remaining <= 0) {
      setError("Not enough credits to export the search results.");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/youtube/searchResultsExport`,
        { keyword: searchKeyword, country: countryCode },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );

      setLoading(false);
      setSuccess(response.data.successMessage);

      const fileName = response.data.fileName;
      if (!fileName) {
        return setError("No file available for download.");
      }

      const downloadResponse = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/youtube/searchResultsExport/download`,
        { fileName },
        {
          withCredentials: true,
          responseType: "blob",
        }
      );

      const blob = new Blob([downloadResponse.data], {
        type: "application/octet-stream",
      });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${fileName}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setDownloadUrl(downloadUrl);
      setCredits({
        remaining: response.data.remaining_credits,
        availed: response.data.availed_credits,
        total: response.data.total_credits,
        loading: false,
      });
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to export search results."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {isLoggedIn && (
        <CreditsBanner credits={credits} setCredits={setCredits} />
      )}
      <CssBaseline />
      <PageContainer sx={{ paddingTop: 3 }}>
        <ContentBox>
          <TitleText variant="h3" align="center">
            Export YouTube Search Results ➡️ CSV
          </TitleText>

          <SubtitleText variant="subtitle1" align="center">
            Download YouTube Search Results in CSV Format
          </SubtitleText>

          <Box mb={3}>
            <TextField
              fullWidth
              label="Search Keyword"
              variant="outlined"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              placeholder="Enter keyword to search"
              size="medium"
              name="primary-textbox"
            />
          </Box>

          <Box mb={3}>
            <Select
              fullWidth
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              name="primary-drop-down"
            >
              {countryOptions.map(({ code, name }) => (
                <MenuItem key={code} value={code}>
                  {name} ({code})
                </MenuItem>
              ))}
            </Select>
          </Box>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
              {downloadUrl && (
                <>
                  <br />
                  If your download hasn't started,{" "}
                  <a href={downloadUrl} download="search_results.csv">
                    click here
                  </a>
                </>
              )}
            </Alert>
          )}

          <Box display="flex" justifyContent="center" gap={2}>
            <StyledButton
              variant="contained"
              color="success"
              onClick={handleExport}
            >
              Export Search Results
            </StyledButton>
          </Box>

          <Box mt={3}>
            <Typography variant="body2" color="text.secondary" align="center">
              <b>💡 1 credit = 1 Full search results export </b>
              <br></br>
              <b>
                In FREE plan you can export up to 5 search results. Upgrade to a
                paid plan for 100 search results per keyword.
              </b>{" "}
              <br></br>
              Enter a search keyword and select a country to export YouTube
              search results. Need help? <Link to="/contact">Contact us</Link>
            </Typography>
          </Box>
        </ContentBox>
        <CommentSection
          toolId="youtube/searchResultsExport"
          user={user}
          login={login}
          isLoggedIn={isLoggedIn}
        />
        <YouTubeSearchResultsExportContent />
      </PageContainer>
    </>
  );
}

export default YouTubeSearchResultsExport;
